import type { HTMLAttributes } from 'react';
import React, { useEffect } from 'react';
import classNames from 'classnames';
import type { ZodFormattedError } from 'zod';
import { ZodError } from 'zod';
import { isObjectType } from '$util/basic';
import { ContentComponentValidationErrorName } from '$cms/ContentComponentFactory/types';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';

import Container from '$components/layout/Container';
import styles from './index.module.scss';

const formatErrorsMap = (
  error: ZodFormattedError<unknown, string>,
  keyParent?: string
): Map<string, string[]> =>
  Object.entries(error as object).reduce((m, [param, errors]) => {
    const composeKey = [keyParent, param].filter((k) => !!k && k !== '_errors').join('.');
    if (isObjectType(errors)) {
      const om = formatErrorsMap(errors as unknown as ZodFormattedError<unknown, string>, composeKey);
      return new Map(Array.from(m).concat(Array.from(om)));
    }
    if (Array.isArray(errors)) {
      errors.forEach((errMsg) => {
        const mErrors = [...(m.get(composeKey) ?? []), errMsg];
        m.set(composeKey, mErrors);
      });
    }
    return m;
  }, new Map<string, string[]>());

const formatErrors = (error: ZodError): [string, string[]][] => Array.from(formatErrorsMap(error.format()));

export interface HighlightContentComponentErrorsProps extends HTMLAttributes<HTMLDivElement> {
  readonly id: string;
  readonly uiComponent: string;
  readonly error: unknown;
}

/**
 * @deprecated use `PreviewErrorFallback` on App Router
 */
export function HighlightContentComponentErrors({
  id,
  uiComponent,
  error,
  className,
  ...props
}: HighlightContentComponentErrorsProps): JSX.Element {
  useEffect(() => {
    /**
     * If it's an unexpected unknown error, log it in the console so we can further investigate
     */
    if (!(error instanceof Error))
      console.error(`Unexpected unknown error for ContentComponent: ${id}`, { uiComponent, error });
  }, [error, id, uiComponent]);

  return (
    <Container
      className={classNames(HighlightContentComponentErrors.displayName, styles.component, className)}
      {...props}
    >
      <div className={styles.title}>
        <HeadingLevels semanticLevel={3}>Content Component</HeadingLevels>
        <code>
          <div>
            <strong>id:</strong>
            {id}
          </div>
          <div>
            <strong>uiComponent:</strong>
            {uiComponent}
          </div>
        </code>
      </div>
      {error instanceof ZodError ? (
        <>
          <HeadingLevels semanticLevel={4}>
            {error.name === ContentComponentValidationErrorName.translatedEntities
              ? 'Translated entities '
              : 'Configuration '}
            errors
          </HeadingLevels>
          <ul className={styles.errors}>
            {formatErrors(error).map(([param, errors]) => {
              return (
                <li key={param} className={styles.errorItem}>
                  <strong className={styles.param}>{param}:</strong>
                  <span className={styles.errorMessages}>
                    {errors.map((errMsg) => (
                      <span key={errMsg} className={styles.errorMessage}>
                        {errMsg}
                      </span>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <>
          <HeadingLevels semanticLevel={2}>Unexpected error</HeadingLevels>
          <p>{error instanceof Error ? error.message : 'unknown error'}</p>
        </>
      )}
    </Container>
  );
}
HighlightContentComponentErrors.displayName = 'HighlightContentComponentErrors';
